import { makeObservable, observable, runInAction, computed, action } from "mobx";
import { ApplicationSdk } from "../..";
import OrderEditDialogStore from "./OrderEditDialogStore";
import { formatDateGerman, formatName } from "../../../helper";

interface Cleaner {
    id: number;
    firstname: string;
    lastname: string;
}
/**
 * The details of a current order
 */
class OrderDialogStore {
    private sdk: ApplicationSdk;

    public order_id: number | null = null;
    public object_id: number | null = null;
    public order_object_name: string | null = null;
    public order_object_street: string | null = null;
    public order_object_housenumber: string | null = null;
    public order_object_zipcode: string | null = null;
    public order_object_city: string | null = null;
    public order_object_note: string | null = null;
    public order_manager: string | null = null;

    public order_created: string | null = null;

    public orderLoading: boolean = false;
    public order_cleaner: Cleaner | null = null;

    public availableProducts: Map<number, any> = new Map();
    public order_status: string | null = null;
    public order_assigned_products: Map<number, any> = new Map();
    public order_comment: string | null = null;

    public originalData: any = null
    public productDialog: boolean = false;

    public addProductAssigned: Array<any> = [];
    public addProductAvailable: Array<any> = [];

    public productDataLoading: boolean = false;

    public s3url: string = '';

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            order_id: observable,
            orderLoading: observable,
            order_cleaner: observable,
            order_object_name: observable,
            order_created: observable,
            availableProducts: observable,
            s3url: observable,
            order_assigned_products: observable,
            order_status: observable,
            order_comment: observable,
            originalData: observable,
            productDialog: observable,
            object_id: observable,
            productDataLoading: observable,
            addProductAssigned: observable,
            addProductAvailable: observable,
            updateStatus: action,
            updateComment: action,
            updateProductUnits: action,
            openAddProductDialog: action,
            closeAddProductDialog: action,
            status: computed,
            chargeableProducts: computed,
            comment: computed,
            buildData: computed,
            hasChanged: computed,
            freeProducts: computed,
            addProductAssignedFiltered: computed,
            addProductAvailableFiltered: computed,
            objectName: computed,
            hasProducts: computed,
            isEditable: computed,
            lagerEntnahmeschein: computed,
            lieferschein: computed
        });

        this.sdk = sdk;
        this.order_cleaner = null;
        this.originalData = null;
    }

    public async init(config: any): Promise<void> {
        this.order_id = config.order_id;
        this.fetchOrder(config.order_id);
    }

    public editCleanerOrObject(): void {
        this.sdk.closeDialog();
        this.sdk.openDialog(OrderEditDialogStore, {
            order_id: this.order_id,
            cleaner_id: this.order_cleaner?.id || null,
            object_id: this.object_id || null
        })
    }

    public async fetchOrder(order_id: number): Promise<void> {
        runInAction(() => {
            this.orderLoading = true;
        });

        const orderDetails: any = await this.sdk.fetchApi(
            'order',
            'get',
            {
                order_id
            });

        runInAction(() => {
            this.orderLoading = false;
            if (orderDetails?.status === 200) {
                const orderData = orderDetails.json;

                if (orderData?.order?.cleaner_id) {
                    this.order_cleaner = {
                        id: orderData.order.cleaner_id,
                        firstname: orderData.order.cleaner_firstname,
                        lastname: orderData.order.cleaner_lastname
                    };
                }

                this.order_status = orderData?.order?.status || 'NEW';
                this.order_comment = orderData?.order?.comment || '';
                this.object_id = orderData?.order?.object_id || null;
                this.order_object_name = `${orderData?.order?.object_name || ''}`
                this.order_object_street = `${orderData?.order?.object_street || ''}`
                this.order_object_housenumber = `${orderData?.order?.object_housenumber || ''}`
                this.order_object_zipcode = `${orderData?.order?.object_zipcode || ''}`
                this.order_object_city = `${orderData?.order?.object_city || ''}`
                this.order_object_note = `${orderData?.order?.object_note || ''}`

                this.order_manager = `${orderData?.order?.manager_firstname || ''} ${orderData?.order?.manager_lastname || ''}`
                this.order_created = orderData?.order?.created_utc || null;

                if (orderData?.products) {
                    orderData.products.forEach((product: any) => {
                        // Add the product to the assigned products
                        if (!this.order_assigned_products.has(product.id)) {
                            this.order_assigned_products.set(product.id, product);
                        }
                    });
                }

                this.s3url = orderData?.s3url || '';
                this.originalData = this.buildData;
            }
        });
    }

    public async fetchProducts(): Promise<void> {
        // Reset
        runInAction(() => {
            this.productDataLoading = true;
        })

        const productList: any = await this.sdk.fetchApi(
            'object',
            'getproducts',
            {
                object_id: this.object_id
            }
        );

        runInAction(() => {
            this.productDataLoading = false;

            if (productList?.status === 200) {
                this.addProductAssigned = productList.json.assigned_products;
                this.addProductAvailable = productList.json.available_products;
            }
        });

    }

    public addProductsToOrder(products: Array<any>): void {
        if (products?.length) {
            products.forEach((product: any) => {
                runInAction(() => {
                    if (!this.order_assigned_products.has(product.id)) {
                        this.order_assigned_products.set(product.id, {
                            id: product.id,
                            name: product.name,
                            description: product.description,
                            image: product.image,
                            chargeable: product.chargeable,
                            article_number: product.article_number,
                            unit_name: product.unit_name,
                            units: 0,
                            units_after: 1
                        });
                    }
                });
            });
        }

    }

    public openAddProductDialog(): void {
        this.productDialog = true;
        this.fetchProducts();
    }

    public closeAddProductDialog(): void {
        this.productDialog = false;
    }

    public async archiveOrder(): Promise<boolean> {
        const saveOrderResult: any = await this.sdk.fetchApi(
            'order',
            'archive',
            {
                order_id: this.order_id
            });

        if (saveOrderResult?.status === 200) {
            return true;
        }
        return false;
    }

    public async onSaveOrder(): Promise<boolean> {
        const saveOrderResult: any = await this.sdk.fetchApi(
            'order',
            'update',
            {
                order_id: this.order_id,
                comment: this.order_comment,
                status: this.order_status,
                order_products: Array.from(this.order_assigned_products.values()).map((product: any) => {
                    return {
                        product_id: product.id,
                        units: product.units,
                        units_after: product.units_after,
                    };
                })
            }
        );

        if (saveOrderResult?.status === 200) {
            return true;
        }
        return false;
    }

    public updateProductUnits(productId: number, delta: number): void {
        const product = this.order_assigned_products.get(productId);
        if (product) {
            const newUnits = (product.units_after || 0) + delta;
            if (newUnits >= 0) { // Ensure units don't go below 0
                product.units_after = newUnits;
                this.order_assigned_products.set(productId, product);
            }
        }
    }

    public updateStatus(status: string): void {
        this.order_status = status;
    }

    public updateComment(comment: string): void {
        this.order_comment = comment;
    }

    /**
     * Get data for the entnahmeschein
     */
    get lagerEntnahmeschein(): any {
        return {
            company: this.order_object_name,
            street: this.order_object_street,
            housenumber: this.order_object_housenumber,
            postalcode: this.order_object_zipcode,
            town: this.order_object_city,
            manager: this.order_manager,
            object_id: this.object_id,
            created: formatDateGerman(this.order_created),
            order_id: this.orderId,
            comment: this.order_comment,
            object_note: this.order_object_note,
            products: this.freeProducts.map((p) => p).filter((p) => p.units_after > 0)
        }
    }

    /**
   * Get data for the lieferschein
   */
    get lieferschein(): any {
        return {
            company: this.order_object_name,
            street: this.order_object_street,
            housenumber: this.order_object_housenumber,
            postalcode: this.order_object_zipcode,
            town: this.order_object_city,
            manager: this.order_manager,
            object_id: this.object_id,
            created: formatDateGerman(this.order_created),
            order_id: this.orderId,
            comment: this.order_comment,
            object_note: this.order_object_note,
            products: this.chargeableProducts.map((p) => p).filter((p) => p.units_after > 0)
        }
    }

    get chargeableProducts(): any[] {
        return Array.from(this.order_assigned_products.values()).filter((product: any) => product.chargeable).map((item: any) => ({
            ...item,
            image: item.image ? `${this.s3url}${item.image}.jpeg` : null
        }));;
    }

    get freeProducts(): any[] {
        return Array.from(this.order_assigned_products.values()).filter((product: any) => !product.chargeable).map((item: any) => ({
            ...item,
            image: item.image ? `${this.s3url}${item.image}.jpeg` : null
        }));;
    }

    get cleanerName(): string {
        if (this.order_cleaner) {
            return formatName(this.order_cleaner.firstname, this.order_cleaner.lastname);
        }
        // this.orderData
        return 'Unbekannt';
    }

    get id(): string {
        return 'order';
    }

    get orderId(): string {
        return `${this.order_id}`.padStart(5, '0');
    }

    get status(): string {
        return this.order_status || '';
    }

    get comment(): string {
        return this.order_comment || '';
    }

    get buildData(): any {
        return {
            status: this.order_status,
            comment: this.order_comment,
            products: Array.from(this.order_assigned_products.values()).map((product: any) => {
                return {
                    product_id: product.id,
                    units: product.units_after
                };
            })
        }
    }

    get hasChanged(): boolean {
        if (!this.originalData) return false;
        // Vergleiche den aktuellen Zustand mit dem ursprünglichen Zustand
        const currentData = JSON.parse(JSON.stringify(this.buildData));
        return JSON.stringify(currentData) !== JSON.stringify(this.originalData);
    }

    get addProductAssignedFiltered(): any[] {
        return this.addProductAssigned.filter((product: any) => !this.order_assigned_products.has(product.id));
    }

    get addProductAvailableFiltered(): any[] {
        return this.addProductAvailable.filter((product: any) => !this.order_assigned_products.has(product.id));
    }

    get objectName(): string {
        return this.order_object_name || '';
    }

    get isEditable(): boolean {
        return true; //this.status === 'NEW';
    }

    get isPrintable(): boolean {
        return this.hasProducts;
    }

    get hasProducts(): boolean {
        return [
            ...(this.freeProducts || []),
            ...(this.chargeableProducts || [])
        ].filter((p) => p.units_after > 0).length > 0;
    }
}

export default OrderDialogStore;